type RolesObject = Record<string, string[]>;

type UserInfo = {
    roles: RolesObject;
    [key: string]: any;
};

const ROLE_GLOBAL = 'global';
const ROLE_ADMIN = 'Admin';
const ROLE_EDITOR = 'Editor';
const ROLE_ANALYST = 'Analyst';
const ROLE_EMPLOYEE = 'Employee';
const ROLE_TOTEAM = 'Toteam';
const ROLE_TOTECH = 'Totech';
const ROLE_TOTEST = 'Totest';

class UserAccessor {
    private static info: UserInfo | null = null;

    setUser(user: UserInfo): void {
        UserAccessor.info = user;
    }

    get(): UserInfo {
        return UserAccessor.info!;
    }

    getGroups(): string[] {
        if (!UserAccessor.info) return [];
        return Object.keys(UserAccessor.info.roles);
    }

    roleInGroup(group: string, rolesObject?: RolesObject): string[] | null {
        if (rolesObject) {
            return rolesObject[group] ?? null;
        }
        if (UserAccessor.info?.roles[group]) {
            return UserAccessor.info.roles[group];
        }
        return null;
    }

    isInRole(role: string, group: string, rolesObject?: RolesObject): boolean {
        if (rolesObject) {
            return rolesObject[group]?.includes(role) ?? false;
        }
        return UserAccessor.info?.roles[group]?.includes(role) ?? false;
    }

    isAdmin(group: string): boolean {
        return this.isInRole(ROLE_ADMIN, group);
    }

    isGlobalAdmin(rolesObject?: RolesObject): boolean {
        return this.isInRole(ROLE_ADMIN, ROLE_GLOBAL, rolesObject);
    }

    isGlobalTeamMember(rolesObject?: RolesObject): boolean {
        return (
            this.isInRole(ROLE_ADMIN, ROLE_GLOBAL, rolesObject) ||
            this.isInRole(ROLE_EDITOR, ROLE_GLOBAL, rolesObject) ||
            this.isInRole(ROLE_ANALYST, ROLE_GLOBAL, rolesObject)
        );
    }

    isGlobalEmployee(rolesObject?: RolesObject): boolean {
        return this.isInRole(ROLE_EMPLOYEE, ROLE_GLOBAL, rolesObject);
    }

    isGlobalToteam(): boolean {
        return this.isInRole(ROLE_TOTEAM, ROLE_GLOBAL) || this.isInRole(ROLE_TOTECH, ROLE_GLOBAL);
    }

    isGlobalTotech(): boolean {
        return this.isInRole(ROLE_TOTECH, ROLE_GLOBAL);
    }

    isGlobalTotest(): boolean {
        return this.isInRole(ROLE_TOTEST, ROLE_GLOBAL);
    }

    isEditor(group: string): boolean {
        return this.isInRole(ROLE_EDITOR, group);
    }

    // A per-totem role is required, like the API (`hasPermissionForTotem`): a global one opens none.
    // Same rule as freefood-app `canAccessTotem` (lib/roles.ts) — keep them in sync.
    canAccessTotem(totemId: string): boolean {
        return this.isAdmin(totemId) || this.isEditor(totemId) || this.isAnalyst(totemId);
    }

    isAnalyst(group: string): boolean {
        return this.isInRole(ROLE_ANALYST, group);
    }

    isEmployee(group: string): boolean {
        return this.isInRole(ROLE_EMPLOYEE, group);
    }

    isAuthenticated(): boolean {
        return UserAccessor.info !== null;
    }
}

export const User = new UserAccessor();
